import type { ReactElement } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@baseline/web/supabase";
import { FiArrowLeft } from "react-icons/fi";
import { formatDate, formatPrice } from "../../lib/format";

export const Route = createFileRoute("/items/$itemId")({
	component: ItemDetail,
});

function ItemDetail(): ReactElement {
	const { itemId } = Route.useParams();

	const { data: item, isLoading } = useQuery({
		queryKey: ["ingest-item", itemId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from("ingest_items")
				.select("*")
				.eq("id", itemId)
				.single();
			if (error) throw error;
			return data;
		},
	});

	if (isLoading) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-8">
				<div className="text-fg-muted text-sm">Loading...</div>
			</div>
		);
	}

	if (!item) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-8">
				<div className="text-fg-muted text-sm">Item not found.</div>
			</div>
		);
	}

	const data = item.data as Record<string, unknown>;

	return (
		<div className="mx-auto max-w-[960px] px-6 py-8">
			<Link
				to="/"
				search={{ tab: "items" }}
				data-testid="back-to-inbox"
				className="text-fg-muted hover:text-accent mb-6 inline-flex items-center gap-1 text-sm transition-colors"
			>
				<FiArrowLeft />
				Back
			</Link>

			<div className="border-line mb-6 rounded-lg border p-6">
				<div className="flex items-center gap-3">
					<h1
						className="text-fg text-xl font-medium"
						data-testid="item-title"
					>
						{item.title || "Untitled"}
					</h1>
					<span className="bg-surface-overlay text-fg-muted rounded px-2 py-0.5 text-xs font-medium uppercase">
						{item.source}
					</span>
				</div>
				<div className="text-fg-muted mt-2 flex items-center gap-3 text-sm">
					<span className="capitalize">
						{item.category.replaceAll("_", " ")}
					</span>
					<span className="text-fg-faint">
						{formatDate(item.created_at)}
					</span>
				</div>
			</div>

			<div className="border-line rounded-lg border p-6">
				<h2 className="text-fg-secondary mb-4 text-sm font-medium tracking-wider uppercase">
					Details
				</h2>
				<dl className="grid grid-cols-2 gap-x-8 gap-y-3">
					{typeof data.address === "string" && (
						<DetailRow label="Address" value={data.address} />
					)}
					{typeof data.neighborhood === "string" && (
						<DetailRow
							label="Neighborhood"
							value={data.neighborhood}
						/>
					)}
					{typeof data.price === "number" && (
						<DetailRow
							label="Price"
							value={`${formatPrice(data.price)}${typeof data.price_label === "string" ? ` (${data.price_label})` : ""}`}
						/>
					)}
					{typeof data.bedrooms === "number" && (
						<DetailRow
							label="Bedrooms"
							value={String(data.bedrooms)}
						/>
					)}
					{typeof data.bathrooms === "number" && (
						<DetailRow
							label="Bathrooms"
							value={String(data.bathrooms)}
						/>
					)}
					{typeof data.sqft === "number" && (
						<DetailRow
							label="Sqft"
							value={data.sqft.toLocaleString()}
						/>
					)}
					{typeof data.price_per_sqft === "number" && (
						<DetailRow
							label="Price/sqft"
							value={`$${data.price_per_sqft}`}
						/>
					)}
					{typeof data.available_date === "string" && (
						<DetailRow
							label="Available"
							value={data.available_date}
						/>
					)}
					{typeof data.listing_agent === "string" && (
						<DetailRow
							label="Listing Agent"
							value={data.listing_agent}
						/>
					)}
					{typeof data.tour_type === "string" && (
						<DetailRow label="Tour Type" value={data.tour_type} />
					)}
				</dl>
			</div>

			{typeof data.description === "string" && (
				<div className="border-line mt-4 rounded-lg border p-6">
					<h2 className="text-fg-secondary mb-3 text-sm font-medium tracking-wider uppercase">
						Description
					</h2>
					<p className="text-fg text-sm leading-relaxed whitespace-pre-wrap">
						{data.description}
					</p>
				</div>
			)}

			<div className="mt-4 flex items-center gap-4">
				{item.source_url && (
					<a
						href={item.source_url}
						target="_blank"
						rel="noopener noreferrer"
						data-testid="view-listing-link"
						className="text-accent text-sm hover:underline"
					>
						View on StreetEasy
					</a>
				)}
				{item.email_id && (
					<Link
						to="/emails/$emailId"
						params={{ emailId: item.email_id }}
						data-testid="view-source-email"
						className="text-accent text-sm hover:underline"
					>
						View source email
					</Link>
				)}
			</div>
		</div>
	);
}

function DetailRow({
	label,
	value,
}: {
	label: string;
	value: string;
}): ReactElement {
	return (
		<div>
			<dt className="text-fg-faint text-xs">{label}</dt>
			<dd className="text-fg text-sm">{value}</dd>
		</div>
	);
}
